/**
 * TKT #274563 · DIRECT native Google sign-in (no Emergent broker, no browser).
 *
 * Flow: begin a serialized attempt → POST /google/start to mint a server nonce →
 * CruiseGoogleAuth.signIn({ nonce }) runs the native Google Sign-In SDK (iOS) /
 * Credential Manager (Android) → the returned Google id_token (whose signed
 * `nonce` claim == the raw server nonce) is exchanged inline at /google/exchange.
 *
 * The id_token's own nonce is the sole attempt binding the backend trusts; a
 * token from another attempt is rejected server-side. Overlapping taps fail
 * closed via beginSignInAttempt(serialize). Start/SDK failure cancels the
 * attempt (client + server) and rethrows so the caller releases the button.
 *
 * The exported name is kept as `launchNativeGoogleBroker` to avoid caller churn
 * (SsoProviders.jsx, Login.jsx); it no longer uses any broker.
 */
import { beginSignInAttempt, setAttemptServerNonce, cancelSignInAttempt } from "./signInAttempt";
import { getDeviceId } from "./deviceId";
import { api } from "@/lib/api";
import { CruiseGoogleAuth, GOOGLE_SERVER_CLIENT_ID } from "./cruiseGoogleAuth";
import { handleGoogleMobileHandoff } from "./mobileAuthSession";

export async function launchNativeGoogleBroker() {
  // One flow at a time. An overlap poisons the attempt so no result completes.
  // Capture THIS attempt's id and bind nonce/cancel/consume to it, so a slow
  // /start, a late SDK result or an error can never affect a newer attempt.
  const attemptId = beginSignInAttempt("google", { serialize: true });
  if (!attemptId) return Promise.resolve();
  let nonce = null;
  try {
    const { data } = await api.post(
      "/api/auth/mobile/google/start",
      {},
      { headers: { "X-CC-Device-Id": getDeviceId() }, _skipAuthRetry: true },
    );
    if (!data?.attempt_nonce) throw new Error("google_start_malformed_response");
    nonce = data.attempt_nonce;
    // If this attempt was cancelled or superseded while /google/start was in
    // flight, attachment fails — STOP before invoking the native SDK and cancel
    // the just-minted server attempt. Fail closed (no session, no error toast).
    if (!setAttemptServerNonce(attemptId, nonce)) {
      try {
        await api.post("/api/auth/mobile/google/cancel", { attempt_nonce: nonce }, { _skipAuthRetry: true });
      } catch { /* best effort */ }
      return Promise.resolve();
    }

    // Native sign-in. The raw server nonce is passed verbatim (see cruiseGoogleAuth.js).
    const res = await CruiseGoogleAuth.signIn({
      nonce,
      serverClientId: GOOGLE_SERVER_CLIENT_ID,
      filterByAuthorizedAccounts: false,
    });
    const idToken = res && res.idToken;
    if (!idToken) throw new Error("google_signin_no_id_token");

    // Inline exchange — no deep link, no AuthCallback leg for native Google.
    return await handleGoogleMobileHandoff({ id_token: idToken, attempt_nonce: nonce, attempt_id: attemptId });
  } catch (err) {
    // Release the flow so the button re-enables; cancel the server attempt so a
    // late/duplicate result can never be exchanged. Both are scoped to THIS
    // attempt id so a newer flow started meanwhile is untouched.
    cancelSignInAttempt(attemptId);
    if (nonce) {
      try {
        await api.post(
          "/api/auth/mobile/google/cancel",
          { attempt_nonce: nonce },
          { _skipAuthRetry: true },
        );
      } catch { /* best effort */ }
    }
    throw err;
  }
}
